import {
  Appear,
  Buttons,
  Flex,
  hooks,
  Icon,
  Link,
  ProgressButton,
  ProgressSpinner,
  Text,
} from '@bitrise/bitkit';
import { TypeMetricKey } from '@trace/types';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { METRIC_NAME_BY_KEY } from '../../../constants/Metrics';
import dataAtIds from '../../../dataAtIds.json';

import { formatter1 } from '../../../utils/metric/getFormattedValue';
import { trackEvent } from '../../../utils/tracking';
import Attributes from '../../Attributes/Attributes';
import Filters from '../../Filters/Filters';
import FiltersSelector from '../../Filters/FiltersSelector';
import Page from '../../Page/Page';
import PageBody from '../../Page/PageBody';
import PageBreadcrumb from '../../Page/PageBreadcrumb';
import PageHeader from '../../Page/PageHeader';
import PageTitle from '../../Page/PageTitle';
import SessionViewer from '../../SessionViewer/SessionViewer';
import useSessions from '../../SessionViewer/useSessions';
import TimeRange from '../../TimeRange/TimeRange';
import { URLStateContext } from '../../URLState/URLState';

const { useMediaQuery } = hooks;

interface Params {
  appId: string;
  metric: TypeMetricKey;
  sessionId: string;
}

const Session: FunctionComponent = () => {
  const { appId, metric, sessionId } = useParams<Params>() as Params;
  const { push, replace } = useContext(URLStateContext);
  const match = useMediaQuery(['1056px']);
  const { isEmpty, isError, isFetched, isFetching, ids, offset, refetch, total } = useSessions(
    sessionId,
  );
  const hasNext = ids.next !== null;
  const hasPrevious = ids.previous !== null;

  useEffect(() => {
    trackEvent('SessionExplorer.Visit', {});
  }, []);

  useEffect(() => {
    if (ids.current) {
      replace(`/${appId}/appPerformance/${metric}/session/${ids.current}`);
    }
  }, [appId, ids, metric, replace]);

  const handleNextSession = () => {
    if (ids.next) {
      trackEvent('SessionExplorer.Navigate.Next', {});
      push(`/${appId}/appPerformance/${metric}/session/${ids.next}`);
    }
  };

  const handlePreviousSession = () => {
    if (ids.previous) {
      trackEvent('SessionExplorer.Navigate.Previous', {});
      push(`/${appId}/appPerformance/${metric}/session/${ids.previous}`);
    }
  };

  const handleResetPagination = () => {
    push(`/${appId}/appPerformance/${metric}/session`);
  };

  return (
    <Attributes source="trace">
      <Page data-trace-at-id={dataAtIds.PerformanceSessionsPage}>
        <PageHeader>
          <Flex alignChildrenVertical="end" direction="horizontal" gap="x4">
            <Flex grow initial="none">
              <PageBreadcrumb to={`/${appId}/appPerformance/${metric}`}>
                {METRIC_NAME_BY_KEY[metric]}
              </PageBreadcrumb>

              <PageTitle>Sessions</PageTitle>
            </Flex>

            {!!total && (
              <Flex>
                <Text textColor="gray-6">
                  {offset + 1} of {formatter1(total, 2)} Sessions
                </Text>
              </Flex>
            )}

            <Flex>
              <Buttons>
                <ProgressButton
                  data-trace-at-id={dataAtIds.SessionExplorerPrevSession}
                  disabled={isFetching || !hasPrevious}
                  level="secondary"
                  onClick={handlePreviousSession}
                  progressing={isFetching && sessionId === ids.previous}
                  size="small"
                >
                  <Icon name="ArrowBack" />
                </ProgressButton>

                <ProgressButton
                  data-trace-at-id={dataAtIds.SessionExplorerNextSession}
                  disabled={isFetching || !hasNext}
                  level="secondary"
                  onClick={handleNextSession}
                  progressing={isFetching && sessionId === ids.next}
                  size="small"
                >
                  <Icon name="ArrowForward" />
                </ProgressButton>
              </Buttons>
            </Flex>
          </Flex>

          <Flex
            alignChildrenHorizontal="between"
            direction={match('1056px') ? 'horizontal' : 'vertical'}
            gap="x4"
            margin="x5"
            reverse={!match('1056px')}
          >
            <Flex grow>
              <FiltersSelector onChange={handleResetPagination} />
            </Flex>

            <Flex>
              <TimeRange onChange={handleResetPagination} />
            </Flex>
          </Flex>

          <Filters onChange={handleResetPagination} />
        </PageHeader>

        <PageBody data-trace-at-id={dataAtIds.PerformanceSessionsPage}>
          {isError && (
            <Flex alignChildrenVertical="middle" direction="vertical" grow>
              <Appear animation="Fade" textColor="red-3">
                <Flex alignChildren="middle" direction="vertical" height="100%">
                  <Flex>
                    <Flex alignChildren="middle" direction="vertical">
                      <Icon margin="x1" name="BitbotFailed" size="3rem" />
                    </Flex>
                    <Text align="middle" ellipsis>
                      Uh oh, there&apos;s been a problem.
                    </Text>
                    <Text align="middle">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <Link clickable onClick={refetch} underline>
                        Click here to retry
                      </Link>
                      .
                    </Text>
                  </Flex>
                </Flex>
              </Appear>
            </Flex>
          )}

          {isEmpty && (
            <Flex alignChildrenVertical="middle" direction="vertical" grow>
              <Appear animation="Fade">
                <Flex alignChildren="middle" direction="vertical" height="100%">
                  <Flex>
                    <Text align="middle">There are no sessions.</Text>
                    <Text align="middle">Try expanding your selected time range and filters.</Text>
                  </Flex>
                </Flex>
              </Appear>
            </Flex>
          )}

          {!isFetched && isFetching && (
            <Flex alignChildren="middle" direction="vertical" grow>
              <Appear animation="Fade" textColor="grape-3">
                <ProgressSpinner />
              </Appear>
            </Flex>
          )}

          {isFetched && (
            <Flex direction="vertical" grow>
              {ids.current && (
                <SessionViewer id={ids.current} onNextSessionClick={handleNextSession} />
              )}
            </Flex>
          )}
        </PageBody>
      </Page>
    </Attributes>
  );
};

export default Session;
